import { fetchAction, GENERIC_ERROR, isValidEmail } from '../utilities'
import { createTurnstile } from '../turnstile'

const CLICK_ACTION = '/actions/expansion/turkey-rot/click'
const TOTAL_ACTION = '/actions/expansion/turkey-rot/get-total'

// Matches the server-side cache TTL — polling faster just re-fetches the same
// cached value.
const POLL_INTERVAL = 60000

const currency = (value) => `$${Number(value || 0).toLocaleString('en-US')}`

/**
 * Turkey Rot campaign header: the donation gauge and the rot button.
 *
 * The email signup block calls the same endpoint from its own component, since
 * the two now live in separate blocks and can't share an Alpine scope. Passing
 * an address to `rot()` still works and is what that block posts. Either way
 * one click comes out of the daily budget, which the server enforces.
 */
export const turkeyRot = (config = {}) => ({
	entryId: config.entryId ?? null,
	csrfToken: config.csrfToken ?? '',
	turnstileSiteKey: config.turnstileSiteKey ?? '',
	confirmationUrl: config.confirmationUrl ?? '/turkey-rot/thanks',

	total: config.total ?? 0,
	goal: config.goal ?? null,
	remaining: config.remaining ?? 5,

	email: '',
	loading: false,
	error: null,
	message: '',

	pollTimer: null,
	turnstile: null,

	get percentRaised() {
		if (!this.goal || this.goal <= 0) {
			return '100%'
		}

		const percent = Math.round((this.total / this.goal) * 100)

		return `${Math.max(0, Math.min(100, percent))}%`
	},

	get formattedTotal() {
		return currency(this.total)
	},

	get formattedGoal() {
		return currency(this.goal)
	},

	/**
	 * Eases the gauge to a new value rather than snapping.
	 */
	tweenNumber(start, end, duration = 1500) {
		const startTime = performance.now()
		const difference = end - start

		if (difference === 0) {
			return
		}

		const animate = (currentTime) => {
			const progress = Math.min((currentTime - startTime) / duration, 1)
			// Ease out cubic
			const eased = 1 - Math.pow(1 - progress, 3)

			this.total = Math.round(start + difference * eased)

			if (progress < 1) {
				requestAnimationFrame(animate)
			}
		}

		requestAnimationFrame(animate)
	},

	/**
	 * Registers a click. Pass an address to also join the Iterable list.
	 */
	async rot(email = null) {
		if (this.loading) {
			return
		}

		const address = typeof email === 'string' ? email.trim() : ''

		// Catch obvious typos before spending a Turnstile token on them.
		if (address !== '' && !isValidEmail(address)) {
			this.error = 'email'
			this.message = 'Please enter a valid email address.'

			return
		}

		this.loading = true
		this.error = null
		this.message = ''

		const turnstileToken = await this.turnstile(this.$refs.turnstile)

		const response = await fetchAction(
			CLICK_ACTION,
			'POST',
			this.csrfToken,
			JSON.stringify({
				entryId: this.entryId,
				turnstileToken,
				email: address !== '' ? address : null,
			}),
		)

		this.loading = false

		if (!response.isSuccessful) {
			this.error = 'network'
			this.message = GENERIC_ERROR

			return
		}

		const data = response.successData ?? {}

		if (!data.success) {
			this.error = data.error ?? 'unknown'
			this.message = data.message ?? GENERIC_ERROR

			// A refusal still reports the current total — keep the gauge honest.
			if (typeof data.total === 'number') {
				this.tweenNumber(this.total, data.total)
			}

			return
		}

		this.remaining = data.remaining ?? this.remaining

		if (typeof data.total === 'number') {
			this.tweenNumber(this.total, data.total)
		}

		// Let the gauge animation land before leaving the page.
		window.setTimeout(() => {
			window.location.href = this.confirmationUrl
		}, 900)
	},

	async fetchTotal() {
		const response = await fetchAction(
			TOTAL_ACTION,
			'POST',
			this.csrfToken,
			JSON.stringify({ entryId: this.entryId }),
		)

		if (!response.isSuccessful) {
			return
		}

		const data = response.successData ?? {}

		if (!data.success) {
			return
		}

		if (typeof data.remaining === 'number') {
			this.remaining = data.remaining
		}

		if (typeof data.total === 'number' && data.total !== this.total) {
			this.tweenNumber(this.total, data.total)
		}
	},

	init() {
		this.turnstile = createTurnstile(this.turnstileSiteKey)

		this.fetchTotal()
		this.pollTimer = window.setInterval(
			() => this.fetchTotal(),
			POLL_INTERVAL,
		)
	},

	destroy() {
		if (this.pollTimer) {
			window.clearInterval(this.pollTimer)
		}
	},
})
